import React, { useState, useEffect } from "react";
import { auth, storage } from "../../config/firebase";
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { sendEmailVerification, onAuthStateChanged } from "firebase/auth";
import { useUser } from "../../contexts/UserContext";
import { useArtist } from "../../contexts/ArtistContext";
import { updateArtistProfile, sendVerificationCode, checkUserAvailabilityForAuth, verifyPasswordChangeCode } from "../../services/api/user.service";
import { ExternalLink, X, Plus, AlertTriangle, CheckCircle, Pencil, Save } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PLATFORMS } from "../../constants/platforms";
import { buildProfileUpdatePayload } from "../../utils/buildProfileUpdatePayload";
import {
  validateField as validateProfileField,
  validateProfileForm as runProfileFormValidation,
} from "../../utils/profileFormValidation";
import CountryCodeDropdown from "../UI/CountryCodeDropdown";
import ImageCropper from "../UI/ImageCropper";
import { COUNTRY_CODES } from "../../constants/countryCodes";
import { getAuthToken } from "../../utils/getAuthToken";
import { performLogout } from "../../utils/logoutHandler";

const ArtistProfile: React.FC = () => {
  const OTP_RESEND_SECONDS = 30;
  const OTP_EXPIRY_SECONDS = 300;
  const user = auth.currentUser;
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, loading: userLoading, isProfileComplete, refreshProfile } = useUser();
  const { currentArtistId, switchingArtist, hasPermission } = useArtist();
  
  // Check if user has permission to edit profile
  const isChangePasswordRoute = location.pathname.includes('/profile/change-password');
  const isAccountRoute = location.pathname.includes('/profile/account');
  const isArtistProfileRoute = location.pathname.includes('/profile/artist');
  const canEditPermission = hasPermission("profile:edit");
  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [isEditingArtist, setIsEditingArtist] = useState(false);

  // When on Account Information screen, only allow editing after clicking EDIT.
  // Other sub-pages keep the previous behavior.
  /** Only the account sub-page needs reduced top padding (matches the sticky header spacing). */
  const isProfileSubRoute = isAccountRoute;

  useEffect(() => {
    if (!isAccountRoute || !canEditPermission) {
      setIsEditingAccount(false);
    }
  }, [isAccountRoute, canEditPermission]);

  useEffect(() => {
    if (!isArtistProfileRoute || !canEditPermission) {
      setIsEditingArtist(false);
    }
  }, [isArtistProfileRoute, canEditPermission]);
  
  // Track previous artistId to detect switches
  const previousArtistIdRef = React.useRef<number | null>(null);
  const [profileData, setProfileData] = useState({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    artistName: "",
    spotifyUrl: "",
    distributor: "",
    mobile: "",
    address: "",
    country: "",
    state: "",
    city: "",
    street: "",
    zipCode: "",
    shirtSize: "",
    youtubeUrl: "",
    appleUrl: "",
    instagramUrl: "",
    facebookUrl: "",
    tiktokUrl: "",
    soundcloudUrl: "",
    twitterUrl: "",
    amazonMusicUrl: "",
    deezerUrl: "",
    audiomackUrl: "",
    pandoraUrl: "",
    tidalUrl: "",
    profilePic: "",
  });
  const [selectedCountryCode, setSelectedCountryCode] = useState("+1");
  const [originalCountryCode, setOriginalCountryCode] = useState("+1");
  const [distributors, setDistributors] = useState<string[]>([]); // Array of distributor names
  const [originalDistributors, setOriginalDistributors] = useState<string[]>([]); // Original distributors for comparison
  const [selectedDistributorDropdown, setSelectedDistributorDropdown] = useState(""); // Selected value from dropdown
  const [customDistributorInput, setCustomDistributorInput] = useState(""); // Input for custom distributor when "Other" is selected
  const [originalProfileData, setOriginalProfileData] = useState({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    artistName: "",
    spotifyUrl: "",
    distributor: "",
    mobile: "",
    address: "",
    country: "",
    state: "",
    city: "",
    street: "",
    zipCode: "",
    shirtSize: "",
    youtubeUrl: "",
    appleUrl: "",
    instagramUrl: "",
    facebookUrl: "",
    tiktokUrl: "",
    soundcloudUrl: "",
    twitterUrl: "",
    amazonMusicUrl: "",
    deezerUrl: "",
    audiomackUrl: "",
    pandoraUrl: "",
    tidalUrl: "",
    profilePic: "",
  });
  const [verificationCode, setVerificationCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingProfilePic, setIsUploadingProfilePic] = useState(false);
  const [profilePicUploadProgress, setProfilePicUploadProgress] = useState(0);

  // While the update API is running, prevent any form edits (inputs + onChange guards).
  const canEditProfile =
    canEditPermission &&
    !isSubmitting &&
    (isAccountRoute ? isEditingAccount : isArtistProfileRoute ? isEditingArtist : true);
  const [profilePicPreviewUrl, setProfilePicPreviewUrl] = useState("");
  const [originalProfilePicPreviewUrl, setOriginalProfilePicPreviewUrl] = useState("");
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<File | null>(null);
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [showVerifyOtpAction, setShowVerifyOtpAction] = useState(false);
  const [hasRequestedOtp, setHasRequestedOtp] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [verificationSessionCountdown, setVerificationSessionCountdown] = useState(0);
  const [resendAvailableAt, setResendAvailableAt] = useState<number | null>(null);
  const [otpExpiresAt, setOtpExpiresAt] = useState<number | null>(null);
  const [otpSentToLabel, setOtpSentToLabel] = useState("");
  const [otpVerificationStatus, setOtpVerificationStatus] = useState<'idle' | 'verified' | 'failed'>('idle');
  const [verifiedOtpValue, setVerifiedOtpValue] = useState("");
  const [verificationSessionToken, setVerificationSessionToken] = useState("");
  const [verificationSessionExpiresAt, setVerificationSessionExpiresAt] = useState<number | null>(null);
  const [mobileAvailabilityStatus, setMobileAvailabilityStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const uploadTaskRef = React.useRef<any>(null);
  const uploadTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const [hasProcessedVerification, setHasProcessedVerification] = useState(() => {
    // Check if we've already processed verification for this user
    const userId = auth.currentUser?.uid;
    if (userId) {
      return localStorage.getItem(`emailVerified_${userId}`) === 'true';
    }
    return false;
  });
  // Reactive email verification state: start false, only set from auth listener to avoid stale cache
  const [emailVerified, setEmailVerified] = useState(false);
  // Validation states
  const [errors, setErrors] = useState({
    username: "",
    firstName: "",
    lastName: "",
    artistName: "",
    spotifyUrl: "",
    mobile: "",
    address: "",
  });

  // Refresh profile data when component mounts or artist changes to ensure fresh data
  useEffect(() => {
    // Detect artist switch
    const artistSwitched = previousArtistIdRef.current !== null && 
                          previousArtistIdRef.current !== currentArtistId;
    
    // Update previous artist ID
    previousArtistIdRef.current = currentArtistId;
    
    // Always refresh profile when component mounts or artist changes to get latest data
    if (!userLoading) {
      // If artist switched, show loading state immediately
      if (artistSwitched) {
        setIsLoading(true);
      }
      
      refreshProfile().catch((error) => {
        console.error("Error refreshing profile on mount or artist change:", error);
      });
    }
  }, [currentArtistId, userLoading, refreshProfile]); // Refetch when artist changes

  // Cleanup upload timeout and task on unmount
  useEffect(() => {
    return () => {
      // Clear timeout if component unmounts during upload
      if (uploadTimeoutRef.current) {
        clearTimeout(uploadTimeoutRef.current);
        uploadTimeoutRef.current = null;
      }
      // Cancel upload task if component unmounts during upload
      if (uploadTaskRef.current) {
        uploadTaskRef.current.cancel();
        uploadTaskRef.current = null;
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  useEffect(() => {
    // Wait for loading to complete and userProfile to be available
    if (userLoading) {
      return; // Still loading, don't do anything yet
    }

    // If no user profile, wait (might be loading)
    if (!userProfile) {
      return;
    }

    // If artist is switching, wait for switch to complete
    if (switchingArtist) {
      setIsLoading(true);
      return;
    }

    // Find the artist in userProfile.artist array that matches currentArtistId
    // This prevents showing data from wrong artist (flash issue)
    let matchingArtist = null;
    if (currentArtistId && userProfile.artist && Array.isArray(userProfile.artist)) {
      matchingArtist = userProfile.artist.find(
        artist => artist.artist_id && parseInt(artist.artist_id, 10) === currentArtistId
      );
    }

    // If no matching artist found and currentArtistId is set, but we have artist data, use fallback
    // Only show loading if we have no artist data at all (waiting for API response)
    if (currentArtistId && !matchingArtist) {
      // If we have artist data but no match, it might be a data structure issue
      // Allow fallback to first artist if available, otherwise wait
      if (!userProfile.artist || userProfile.artist.length === 0) {
        setIsLoading(true);
        return; // Wait for profile refresh to complete
      }
      // If we have artist data but no exact match, proceed with fallback
      // This handles cases where artist_id format might differ
    }

    // Use matching artist if found, otherwise fallback to first artist (for backward compatibility)
    const artistData = matchingArtist || userProfile.artist?.[0];
    
    // If still no artist data available, show loading
    if (!artistData && currentArtistId) {
      setIsLoading(true);
      return;
    }

    // Parse mobile number to extract country code - handles all country codes dynamically
    let mobileNumber = "";
    let countryCode = "+1";
    if (userProfile?.mobile) {
      const mobile = userProfile.mobile;
      
      // Handle different mobile number formats
      if (mobile.startsWith('+')) {
          // Mobile starts with +, extract country code by matching against known codes
          // Try longest codes first (3 digits, then 2, then 1) to avoid false matches
          const allDigits = mobile.substring(1).replace(/\D/g, '');
          
          let matched = false;
          
          // Try 3-digit country codes first (e.g., +212, +213, +216, +220, etc.)
          if (allDigits.length >= 10) {
            const code3 = '+' + allDigits.substring(0, 3);
            if (COUNTRY_CODES.some(cc => cc.code === code3)) {
              countryCode = code3;
              mobileNumber = allDigits.substring(3);
              matched = true;
            }
          }
          
          // Try 2-digit country codes if 3-digit didn't match (e.g., +20, +27, +30, +44, +84, etc.)
          if (!matched && allDigits.length >= 9) {
            const code2 = '+' + allDigits.substring(0, 2);
            if (COUNTRY_CODES.some(cc => cc.code === code2)) {
              countryCode = code2;
              mobileNumber = allDigits.substring(2);
              matched = true;
            }
          }
          
          // Try 1-digit country codes if 2-digit didn't match (e.g., +1, +7)
          if (!matched && allDigits.length >= 8) {
            const code1 = '+' + allDigits.substring(0, 1);
            if (COUNTRY_CODES.some(cc => cc.code === code1)) {
              countryCode = code1;
              mobileNumber = allDigits.substring(1);
              matched = true;
            }
          }
          
          // If no match found, default to using all digits as mobile number
          if (!matched) {
            mobileNumber = allDigits;
          }
        } else if (mobile.length >= 10) {
          // Mobile doesn't start with +, try to infer country code
          // If starts with 1 and length is 11, likely US/Canada
          if (mobile.startsWith('1') && mobile.length === 11) {
            countryCode = '+1';
            mobileNumber = mobile.substring(1);
          } else {
            // No country code prefix, use full number
            mobileNumber = mobile.replace(/\D/g, '');
          }
        } else {
          // Short number, treat as mobile number only
          mobileNumber = mobile.replace(/\D/g, '');
        }
      }
      
    // Parse distributors from comma-separated string
    const distributorValue = artistData?.distributor || "";
    let initialDistributors: string[] = [];
    
    if (distributorValue) {
      // Split by comma and trim each value
      const distributorArray = distributorValue.split(',').map(d => d.trim()).filter(d => d.length > 0);
      
      // Normalize each distributor (check if it matches a platform name case-insensitively)
      initialDistributors = distributorArray.map(dist => {
        const matchedPlatform = PLATFORMS.find(p => p.name.toLowerCase() === dist.toLowerCase());
        return matchedPlatform ? matchedPlatform.name : dist; // Use platform name if matched, otherwise keep original
      });
    }
    
    // Helper: extract storage path from a download URL
    const decodeStoragePathFromUrl = (url: string): string => {
      try {
        const oIndex = url.indexOf('/o/');
        if (oIndex === -1) return url;
        const afterO = url.substring(oIndex + 3);
        const pathEnc = afterO.split('?')[0];
        return decodeURIComponent(pathEnc);
      } catch {
        return url;
      }
    };

    const profilePicRaw = userProfile.profile_pic || "";
    const profilePicPath = profilePicRaw.startsWith('http') ? decodeStoragePathFromUrl(profilePicRaw) : profilePicRaw;

    const initialData = {
      username: userProfile.username || "",
      email: userProfile.email || "",
      firstName: userProfile.firstname || "",
      lastName: userProfile.lastname || "",
      artistName: artistData?.artist_name || "",
      spotifyUrl: artistData?.spotify_url || "",
      distributor: "", // Keep for backward compatibility, but we'll use distributors array
      mobile: mobileNumber,
      address: userProfile.address || "",
      country: (userProfile as any).country || "",
      state: (userProfile as any).state || "",
      city: (userProfile as any).city || "",
      street: (userProfile as any).street || "",
      zipCode: (userProfile as any).zip_code || "",
      shirtSize: userProfile.shirt_size || "",
      youtubeUrl: artistData?.youtube_url || "",
      appleUrl: artistData?.apple_url || "",
      instagramUrl: artistData?.instagram_url || "",
      facebookUrl: artistData?.facebook_url || "",
      tiktokUrl: artistData?.tiktok_url || "",
      soundcloudUrl: artistData?.soundcloud_url || "",
      twitterUrl: artistData?.twitter_url || "",
      amazonMusicUrl: artistData?.amazon_music_url || "",
      deezerUrl: artistData?.deezer_url || "",
      audiomackUrl: artistData?.audiomack_url || "",
      pandoraUrl: artistData?.pandora_url || "",
      tidalUrl: artistData?.tidal_url || "",
      profilePic: profilePicPath,
    };
    
    setSelectedCountryCode(countryCode);
    setOriginalCountryCode(countryCode);
    setDistributors(initialDistributors);
    setOriginalDistributors([...initialDistributors]);
    
    setProfileData(initialData);
    // Set preview URL: if original was a URL use it; else attempt to resolve from storage path
    if (profilePicRaw.startsWith('http')) {
      setProfilePicPreviewUrl(profilePicRaw);
      setOriginalProfilePicPreviewUrl(profilePicRaw);
    } else if (profilePicPath) {
      try {
        const refPath = storageRef(storage, profilePicPath);
        getDownloadURL(refPath)
          .then((url) => {
            setProfilePicPreviewUrl(url);
            setOriginalProfilePicPreviewUrl(url);
          })
          .catch(() => {
            setProfilePicPreviewUrl("");
            setOriginalProfilePicPreviewUrl("");
          });
      } catch {
        setProfilePicPreviewUrl("");
        setOriginalProfilePicPreviewUrl("");
      }
    } else {
      setProfilePicPreviewUrl("");
      setOriginalProfilePicPreviewUrl("");
    }
    setOriginalProfileData(initialData);
    
    // Always set loading to false once we have data to display
    setIsLoading(false);
  }, [userLoading, userProfile, navigate, refreshProfile, currentArtistId, switchingArtist]);
  
  // Additional safeguard: if userProfile is loaded and we have artist data, ensure loading is false
  // This prevents the page from staying in loading state when data is available
  useEffect(() => {
    if (!userLoading && userProfile && !switchingArtist) {
      // If we have profile data and not switching, check if we should stop loading
      if (userProfile.artist && userProfile.artist.length > 0) {
        // We have artist data available
        if (currentArtistId) {
          // Check if we have a matching artist or any artist data to show
          const hasMatchingArtist = userProfile.artist.some(artist => 
            artist.artist_id && parseInt(artist.artist_id, 10) === currentArtistId
          );
          // If we have matching artist or any artist data, stop loading
          if (hasMatchingArtist || userProfile.artist.length > 0) {
            setIsLoading(false);
          }
        } else {
          // No currentArtistId, but we have artist data - stop loading
          setIsLoading(false);
        }
      }
    }
  }, [userLoading, userProfile, currentArtistId, switchingArtist]);

  // Listen for email verification changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setEmailVerified(user?.emailVerified ?? false);
      if (user) {
        const userId = user.uid;
        const storedVerificationState = localStorage.getItem(`emailVerified_${userId}`);
        
        if (user.emailVerified && !hasProcessedVerification && storedVerificationState !== 'true') {
          setHasProcessedVerification(true);
          // Persist the verification state to prevent showing toast on subsequent loads
          localStorage.setItem(`emailVerified_${userId}`, 'true');
          
          try {
            await refreshProfile();
            //toast.success("Email verified successfully!", { toastId: "email-verified" });
          } catch (error) {
            console.error("Error updating email verification status:", error);
            toast.error("Failed to update email verification status", { toastId: "email-verify-error" });
          }
        } else if (!user.emailVerified && storedVerificationState === 'true') {
          // Reset verification state if email becomes unverified
          localStorage.removeItem(`emailVerified_${userId}`);
          setHasProcessedVerification(false);
        }
      } else {
        // User logged out, clean up verification state
        const userId = auth.currentUser?.uid;
        if (userId) {
          localStorage.removeItem(`emailVerified_${userId}`);
        }
      }
    });

    return () => unsubscribe();
  }, [refreshProfile, hasProcessedVerification]);

  // Mobile availability check with debouncing
  const checkMobileAvailability = async (mobile: string, countryCode?: string) => {
    const cleanNumber = mobile.replace(/\D/g, '');
    if (!cleanNumber || cleanNumber.length < 10 || cleanNumber.length > 12) {
      setMobileAvailabilityStatus('idle');
      return;
    }

    const codeToUse = countryCode || selectedCountryCode;
    // Don't check if it's the same as current mobile
    const fullMobileNumber = `${codeToUse}${mobile}`;
    if (fullMobileNumber === originalProfileData.mobile) {
      setMobileAvailabilityStatus('available');
      return;
    }

    setMobileAvailabilityStatus('checking');

    try {
      // Use authenticated check to exclude current user from availability check
      const userId = userProfile?.id?.toString() || '';
      const response = await checkUserAvailabilityForAuth('mobile', fullMobileNumber, userId);
      if (response.exists) {
        setMobileAvailabilityStatus('taken');
        setErrors(prev => ({...prev, mobile: 'Mobile number is already registered'}));
      } else {
        setMobileAvailabilityStatus('available');
        setErrors(prev => ({...prev, mobile: ''}));
      }
    } catch (error) {
      console.error('Error checking mobile availability:', error);
      setMobileAvailabilityStatus('idle');
    }
  };

  // Debounced mobile check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const fullMobileNumber = `${selectedCountryCode}${profileData.mobile}`;
      if (profileData.mobile && fullMobileNumber !== originalProfileData.mobile) {
        checkMobileAvailability(profileData.mobile);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [profileData.mobile, selectedCountryCode]);

  const profileValidationAvailability = React.useMemo(
    () => ({
      usernameTaken: false,
      mobileTaken: mobileAvailabilityStatus === "taken",
    }),
    [mobileAvailabilityStatus]
  );

  const validatePasswordFields = (forceRequired = false): boolean => {
    const newPasswordErrors = {
      newPassword: "",
      confirmPassword: "",
    };

    // Match signup rules:
    // - Password is required
    // - Password must be at least 6 characters
    // - Confirm Password is required
    // - Passwords must match
    if (forceRequired || newPassword.trim() || confirmPassword.trim()) {
      if (!newPassword.trim()) {
        newPasswordErrors.newPassword = "Password is required";
      } else if (newPassword.length < 6) {
        newPasswordErrors.newPassword = "Password must be at least 6 characters";
      }

      if (!confirmPassword.trim()) {
        newPasswordErrors.confirmPassword = "Confirm Password is required";
      } else if (confirmPassword.trim() !== newPassword.trim()) {
        newPasswordErrors.confirmPassword = "Passwords do not match";
      }
    }

    setPasswordErrors(newPasswordErrors);
    return !Object.values(newPasswordErrors).some(error => error !== '');
  };

  // Real-time validation for password fields
  const handlePasswordChange = (field: 'newPassword' | 'confirmPassword', value: string) => {
    if (field === 'newPassword') {
      setNewPassword(value);
    } else {
      setConfirmPassword(value);
    }
  };

  // Validate password fields when they change
  useEffect(() => {
    validatePasswordFields();
  }, [newPassword, confirmPassword]);

  useEffect(() => {
    if (!resendAvailableAt) {
      setResendCountdown(0);
      return;
    }

    const updateCountdown = () => {
      const remainingSeconds = Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / 1000));
      setResendCountdown(remainingSeconds);
    };

    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);

    return () => window.clearInterval(timer);
  }, [resendAvailableAt]);

  useEffect(() => {
    if (!verificationSessionExpiresAt) {
      setVerificationSessionCountdown(0);
      return;
    }

    const updateCountdown = () => {
      const remainingSeconds = Math.max(0, Math.ceil((verificationSessionExpiresAt - Date.now()) / 1000));
      if (remainingSeconds <= 0) {
        resetChangePasswordVerificationState({ keepOtpRequest: true });
        return;
      }
      setVerificationSessionCountdown(remainingSeconds);
    };

    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);
    return () => window.clearInterval(timer);
  }, [verificationSessionExpiresAt]);

  const isPasswordVerificationActive =
    otpVerificationStatus === 'verified' &&
    Boolean(verificationSessionToken) &&
    Boolean(verificationSessionExpiresAt && verificationSessionExpiresAt > Date.now());
  const isPasswordSectionComplete =
    newPassword.trim() !== "" &&
    confirmPassword.trim() !== "" &&
    newPassword === confirmPassword &&
    !passwordErrors.newPassword &&
    !passwordErrors.confirmPassword;

  useEffect(() => {
    if (isPasswordVerificationActive) return;
    if (!newPassword && !confirmPassword) return;
    setNewPassword("");
    setConfirmPassword("");
    setPasswordErrors({ newPassword: "", confirmPassword: "" });
  }, [isPasswordVerificationActive, newPassword, confirmPassword]);

  const otpUiSessionKey = React.useMemo(() => {
    const identity = userProfile?.id ? String(userProfile.id) : user?.uid;
    return identity ? `profile_change_password_otp_ui_${identity}` : null;
  }, [userProfile?.id, user?.uid]);

  const clearOtpUiSession = () => {
    if (!otpUiSessionKey) return;
    sessionStorage.removeItem(otpUiSessionKey);
  };

  const resetChangePasswordVerificationState = ({ keepOtpRequest = true }: { keepOtpRequest?: boolean } = {}) => {
    setVerificationSessionToken("");
    setVerificationSessionExpiresAt(null);
    setVerificationSessionCountdown(0);
    setOtpVerificationStatus('idle');
    setVerifiedOtpValue("");
    setVerificationCode("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordErrors({ newPassword: "", confirmPassword: "" });
    setShowVerifyOtpAction(keepOtpRequest);
    if (!keepOtpRequest) {
      setHasRequestedOtp(false);
      setResendAvailableAt(null);
      setOtpExpiresAt(null);
      setOtpSentToLabel("");
    }
  };

  const formatSecondsToMinutes = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };


  useEffect(() => {
    if (!otpUiSessionKey) return;

    try {
      const raw = sessionStorage.getItem(otpUiSessionKey);
      if (!raw) return;

      const parsed = JSON.parse(raw) as {
        hasRequestedOtp?: boolean;
        showVerifyOtpAction?: boolean;
        resendAvailableAt?: number | null;
        otpExpiresAt?: number | null;
        otpSentToLabel?: string;
        otpVerified?: boolean;
        verifiedOtpValue?: string;
        verificationSessionToken?: string;
        verificationSessionExpiresAt?: number | null;
      };

      const now = Date.now();
      const stillValid = parsed.otpExpiresAt && parsed.otpExpiresAt > now;
      if (!stillValid) {
        sessionStorage.removeItem(otpUiSessionKey);
        return;
      }

      setHasRequestedOtp(Boolean(parsed.hasRequestedOtp));
      setShowVerifyOtpAction(Boolean(parsed.showVerifyOtpAction));
      setResendAvailableAt(parsed.resendAvailableAt || null);
      setOtpExpiresAt(parsed.otpExpiresAt || null);
      setOtpSentToLabel(parsed.otpSentToLabel || "");
      const hasValidVerificationSession = Boolean(
        parsed.verificationSessionToken &&
        parsed.verificationSessionExpiresAt &&
        parsed.verificationSessionExpiresAt > now
      );

      if (parsed.otpVerified && hasValidVerificationSession) {
        setOtpVerificationStatus('verified');
        setVerifiedOtpValue(parsed.verifiedOtpValue || "");
      } else {
        setOtpVerificationStatus('idle');
        setVerifiedOtpValue("");
      }
      if (hasValidVerificationSession) {
        setVerificationSessionToken(parsed.verificationSessionToken || "");
        setVerificationSessionExpiresAt(parsed.verificationSessionExpiresAt || null);
      } else {
        setVerificationSessionToken("");
        setVerificationSessionExpiresAt(null);
      }
    } catch {
      sessionStorage.removeItem(otpUiSessionKey);
    }
  }, [otpUiSessionKey]);

  useEffect(() => {
    if (!otpUiSessionKey) return;
    if (!hasRequestedOtp) {
      sessionStorage.removeItem(otpUiSessionKey);
      return;
    }

    const payload = {
      hasRequestedOtp,
      showVerifyOtpAction,
      resendAvailableAt,
      otpExpiresAt,
      otpSentToLabel,
      otpVerified: otpVerificationStatus === 'verified',
      verifiedOtpValue: otpVerificationStatus === 'verified' ? verifiedOtpValue : "",
      verificationSessionToken,
      verificationSessionExpiresAt,
    };
    sessionStorage.setItem(otpUiSessionKey, JSON.stringify(payload));
  }, [otpUiSessionKey, hasRequestedOtp, showVerifyOtpAction, resendAvailableAt, otpExpiresAt, otpSentToLabel, otpVerificationStatus, verifiedOtpValue, verificationSessionToken, verificationSessionExpiresAt]);

  const validateProfileForm = (): boolean => {
    const { errors: newErrors, isValid } = runProfileFormValidation(profileData, profileValidationAvailability);
    setErrors(newErrors);
    return isValid;
  };

  const hasProfileChanges = (): boolean => {
    // Check for profile data changes
    const profileChanged = (
      profileData.username !== originalProfileData.username ||
      profileData.firstName !== originalProfileData.firstName ||
      profileData.lastName !== originalProfileData.lastName ||
      profileData.artistName !== originalProfileData.artistName ||
      profileData.spotifyUrl !== originalProfileData.spotifyUrl ||
      // Compare distributors arrays
      JSON.stringify([...distributors].sort()) !== JSON.stringify([...originalDistributors].sort()) ||
      profileData.mobile !== originalProfileData.mobile ||
      selectedCountryCode !== originalCountryCode ||
      profileData.address !== originalProfileData.address ||
      profileData.country !== originalProfileData.country ||
      profileData.state !== originalProfileData.state ||
      profileData.city !== originalProfileData.city ||
      profileData.street !== originalProfileData.street ||
      profileData.zipCode !== originalProfileData.zipCode ||
      profileData.shirtSize !== originalProfileData.shirtSize ||
      profileData.youtubeUrl !== originalProfileData.youtubeUrl ||
      profileData.appleUrl !== originalProfileData.appleUrl ||
      profileData.instagramUrl !== originalProfileData.instagramUrl ||
      profileData.facebookUrl !== originalProfileData.facebookUrl ||
      profileData.tiktokUrl !== originalProfileData.tiktokUrl ||
      profileData.soundcloudUrl !== originalProfileData.soundcloudUrl ||
      profileData.twitterUrl !== originalProfileData.twitterUrl ||
      profileData.amazonMusicUrl !== originalProfileData.amazonMusicUrl ||
      profileData.deezerUrl !== originalProfileData.deezerUrl ||
      profileData.audiomackUrl !== originalProfileData.audiomackUrl ||
      profileData.pandoraUrl !== originalProfileData.pandoraUrl ||
      profileData.tidalUrl !== originalProfileData.tidalUrl ||
      profileData.profilePic !== originalProfileData.profilePic
    );

    return profileChanged;
  };

  const resetAccountEdits = () => {
    setProfileData({ ...originalProfileData });
    setSelectedCountryCode(originalCountryCode);
    setDistributors([...originalDistributors]);
    setSelectedDistributorDropdown("");
    setCustomDistributorInput("");
    setProfilePicPreviewUrl(originalProfilePicPreviewUrl);
    setErrors({
      username: "",
      firstName: "",
      lastName: "",
      artistName: "",
      spotifyUrl: "",
      mobile: "",
      address: "",
    });
    setMobileAvailabilityStatus("idle");
    setShowCropModal(false);
    setImageToCrop(null);
  };

  // Add distributor function
  const addDistributor = (distributor: string) => {
    if (!distributor.trim()) return;
    
    const trimmedDistributor = distributor.trim();
    
    // Check if already exists (case-insensitive)
    if (distributors.some(d => d.toLowerCase() === trimmedDistributor.toLowerCase())) {
      toast.error("Distributor already added");
      return;
    }
    
    // Normalize: check if it matches a platform name (case-insensitive)
    const matchedPlatform = PLATFORMS.find(p => p.name.toLowerCase() === trimmedDistributor.toLowerCase());
    const normalizedDistributor = matchedPlatform ? matchedPlatform.name : trimmedDistributor;
    
    setDistributors([...distributors, normalizedDistributor]);
    setSelectedDistributorDropdown(""); // Reset dropdown
    setCustomDistributorInput(""); // Reset custom input
  };

  // Remove distributor function
  const removeDistributor = (index: number) => {
    setDistributors(distributors.filter((_, i) => i !== index));
  };

  // Handle distributor selection from dropdown
  const handleDistributorSelect = (value: string) => {
    if (value === "Other") {
      // Don't add "Other" directly, wait for custom input
      setSelectedDistributorDropdown(value);
    } else if (value) {
      addDistributor(value);
    }
  };

  // Handle adding custom distributor
  const handleAddCustomDistributor = () => {
    if (customDistributorInput.trim()) {
      addDistributor(customDistributorInput.trim());
    } else {
      toast.error("Please enter a distributor name");
    }
  };

  const handleSaveChanges = async (mode: 'profile' | 'password') => {
    // Check permission before allowing save
    if (!canEditPermission) {
      toast.error("You don't have permission to edit this profile. Please contact your administrator.", { toastId: "no-edit-permission" });
      return;
    }

    if (mode === 'profile') {
      if (!validateProfileForm()) {
        toast.error("Please fix validation errors before saving", { toastId: "validation-error" });
        return;
      }
      if (!hasProfileChanges()) {
        toast.info("No changes to save", { toastId: "no-changes" });
        return;
      }
    } else {
      if (!validatePasswordFields(true)) {
        toast.error("Please fix validation errors before saving", { toastId: "password-validation-error" });
        return;
      }
    }

    // Guard password update flow: require verification code and both password fields.
    const trimmedVerificationCode = verificationCode.trim();
    const trimmedNewPassword = newPassword.trim();
    const trimmedConfirmPassword = confirmPassword.trim();
    const hasAnyPasswordInput = Boolean(trimmedNewPassword || trimmedConfirmPassword);
    const hasVerificationCode = Boolean(trimmedVerificationCode);
    const hasValidVerificationSession = Boolean(
      verificationSessionToken &&
      verificationSessionExpiresAt &&
      verificationSessionExpiresAt > Date.now()
    );

    if (mode === 'password' && hasAnyPasswordInput && !hasVerificationCode && !hasValidVerificationSession) {
      toast.error("Verification code is required to change password", { toastId: "verification-code-required" });
      return;
    }

    if (mode === 'password' && hasVerificationCode && !hasAnyPasswordInput) {
      toast.error("Enter new password and confirm password to use verification code", { toastId: "password-fields-required" });
      return;
    }

    if (mode === 'password' && hasAnyPasswordInput && otpVerificationStatus !== 'verified') {
      toast.error("Please verify the OTP before changing password", { toastId: "verify-otp-required" });
      return;
    }

    if (mode === 'password' && hasAnyPasswordInput && !hasValidVerificationSession) {
      setOtpVerificationStatus('failed');
      setVerifiedOtpValue("");
      setVerificationSessionToken("");
      setVerificationSessionExpiresAt(null);
      setShowVerifyOtpAction(true);
      toast.error("Verification session expired. Please verify OTP again", { toastId: "verification-session-expired" });
      return;
    }

    if (mode === 'password' && hasAnyPasswordInput && hasVerificationCode && verifiedOtpValue !== trimmedVerificationCode) {
      toast.error("Verification code changed. Please verify OTP again", { toastId: "verify-otp-changed" });
      return;
    }

    // Validate token before submission
    try {
      await getAuthToken(true); // Force refresh to check validity before save
    } catch (error: any) {
      // Token validation failed - error handling in getAuthToken will trigger logout
      console.error("[ArtistProfile] Token validation failed before save:", error);
      return; // Stop save operation
    }

    setIsSubmitting(true);
    try {
      // Build minimal payload: only send changed fields (shared pure builder + legacy distributor sort side effect)
      const payload: any = {};
      if (mode === 'profile') {
        Object.assign(
          payload,
          buildProfileUpdatePayload(
            {
              profile: profileData,
              distributors,
              countryCode: selectedCountryCode,
            },
            {
              profile: originalProfileData,
              distributors: originalDistributors,
              countryCode: originalCountryCode,
            }
          )
        );
      }
      // Check if password is being changed
      const isPasswordChange = mode === 'password' && Boolean(trimmedNewPassword && trimmedConfirmPassword && hasValidVerificationSession);
      
      if (isPasswordChange) {
        payload.newPassword = trimmedNewPassword;
        payload.verificationCode = trimmedVerificationCode;
        payload.verificationSessionToken = verificationSessionToken;
      }

      await updateArtistProfile(payload);

      // If password was changed, sign out the user automatically
      if (isPasswordChange) {
        toast.success("Password changed successfully. Please login again.", { 
          toastId: "password-changed-success",
          autoClose: 3000,
        });
        
        // Clear password fields
        setNewPassword("");
        setConfirmPassword("");
        setVerificationCode("");
        setHasRequestedOtp(false);
        setShowVerifyOtpAction(false);
        setResendAvailableAt(null);
        setOtpExpiresAt(null);
        setOtpSentToLabel("");
        setVerificationSessionToken("");
        setVerificationSessionExpiresAt(null);
        clearOtpUiSession();
        
        // Sign out after a short delay to show the success message
        setTimeout(async () => {
          await performLogout("Password changed successfully");
        }, 1500);
        return; // Exit early, don't update other UI state
      }

      // Optimistic UI: update originals immediately (only if password wasn't changed)
      setOriginalProfileData({ ...profileData });
      setOriginalCountryCode(selectedCountryCode);
      setOriginalDistributors([...distributors]);
      setOriginalProfilePicPreviewUrl(profilePicPreviewUrl);

      // Exit edit mode after successful profile save.
      if (isAccountRoute) setIsEditingAccount(false);
      if (isArtistProfileRoute) setIsEditingArtist(false);

      // Refresh the user profile in background
      refreshProfile().catch(() => {});

      toast.success("Profile updated successfully", { toastId: "profile-updated" });
    } catch (error: any) {
      console.error("Error updating profile:", error);
      
      // Handle 400 Bad Request errors - show the error message from response
      if (error?.status === 400 && error?.message) {
        const otpErrorMessages = [
          'Invalid or expired verification code',
          'Invalid verification code',
          'Verification is required to change password',
          'Invalid verification session',
          'Verification session expired. Please verify OTP again',
        ];
        if (otpErrorMessages.includes(error.message)) {
          setOtpVerificationStatus('failed');
          setVerifiedOtpValue("");
          setVerificationSessionToken("");
          setVerificationSessionExpiresAt(null);
          setShowVerifyOtpAction(true);
          setHasRequestedOtp(false);
          setResendAvailableAt(null);
          setOtpExpiresAt(null);
          setOtpSentToLabel("");
          setVerificationCode("");
          clearOtpUiSession();
        }
        toast.error(error.message, {
          toastId: "profile-update-error-400",
          autoClose: 5000,
        });
        setIsSubmitting(false);
        return;
      }
      
      // Handle database connection errors (500) - don't logout, just show error
      if (error?.status === 500 && error?.isDatabaseError) {
        toast.error("Database connection error. Please try again.", {
          toastId: "database-error",
          autoClose: 5000,
        });
        setIsSubmitting(false);
        return;
      }
      
      // Handle 401 Unauthorized errors (real auth failures)
      if (error?.status === 401 || error?.isUnauthorized || (error?.message && error.message.includes("Unauthorized"))) {
        // Use centralized logout handler (prevents race conditions)
        await performLogout("401 Unauthorized from profile update");
        setIsSubmitting(false);
        return;
      }
      
      const errorMessage = error?.response?.data?.error?.message || error?.message || "Failed to update profile";
      toast.error(errorMessage, { toastId: "profile-update-error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendVerificationCode = async () => {
    if (!user) {
      toast.error("User not found");
      return;
    }

    setIsSendingVerification(true);
    try {
      //Need to add continueUrl to the actionCodeSettings
      const actionCodeSettings = {
        url: `${process.env.REACT_APP_FRONTEND_URL}/profile`,
        handleCodeInApp: true,
      };
      await sendEmailVerification(user, actionCodeSettings);
      toast.success("Verification email sent! Please check your inbox.", { toastId: "verification-sent" });
      setShowVerifyOtpAction(true);
    } catch (error) {
      console.error("Error sending verification email:", error);
      toast.error("Failed to send verification email. Please try again.", { toastId: "verification-send-error" });
      setShowVerifyOtpAction(false);
    } finally {
      setIsSendingVerification(false);
      setOtpVerificationStatus('idle');
      setVerifiedOtpValue("");
      setVerificationSessionToken("");
      setVerificationSessionExpiresAt(null);
    }
  };

  const handleVerifyOtp = async () => {
    const trimmedCode = verificationCode.trim();
    if (!trimmedCode) {
      toast.error("Please enter verification code", { toastId: "verification-code-empty" });
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const verifyResponse = await verifyPasswordChangeCode(trimmedCode);
      if (!verifyResponse?.verificationSessionToken || !verifyResponse?.verificationTokenExpiresAt) {
        throw new Error("Failed to create verification session. Please try again.");
      }
      setOtpVerificationStatus('verified');
      setVerifiedOtpValue(trimmedCode);
      setVerificationSessionToken(verifyResponse.verificationSessionToken);
      setVerificationSessionExpiresAt(verifyResponse.verificationTokenExpiresAt);
      setShowVerifyOtpAction(false);
    } catch (error: any) {
      setOtpVerificationStatus('failed');
      setVerifiedOtpValue("");
      setVerificationSessionToken("");
      setVerificationSessionExpiresAt(null);
      setShowVerifyOtpAction(true);
      const errorMessage = error?.message || "Invalid or expired verification code";
      toast.error(errorMessage, { toastId: "otp-verified-failed" });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleResetPassword = () => {
    // Implement password reset logic
  };

  const handleResendCode = async () => {
    if (!user) {
      toast.error("User not found");
      return;
    }
    if (resendAvailableAt && resendAvailableAt > Date.now()) {
      return;
    }
    setIsSendingVerification(true);
    setOtpVerificationStatus('idle');
    setVerifiedOtpValue("");
    setVerificationSessionToken("");
    setVerificationSessionExpiresAt(null);
    setShowVerifyOtpAction(false);
    // Implement resend code logic
    try {
      const response = await sendVerificationCode();
      toast.success("Verification code sent! Please check your inbox.", { toastId: "verification-sent" });
      setHasRequestedOtp(true);
      setShowVerifyOtpAction(true);
      const now = Date.now();
      setResendAvailableAt(now + OTP_RESEND_SECONDS * 1000);
      setOtpExpiresAt(now + OTP_EXPIRY_SECONDS * 1000);
      const delivery = response?.delivery;
      const maskedTargets: string[] = [];
      if (delivery?.maskedEmail) maskedTargets.push(delivery.maskedEmail);
      if (delivery?.maskedMobile) maskedTargets.push(delivery.maskedMobile);
      setOtpSentToLabel(maskedTargets.join(" and "));
    } catch (error) {
      console.error("Error sending verification code:", error);
      toast.error("Failed to send verification email. Please try again.", { toastId: "verification-send-error" });
      // Keep verify action available if user already has a previously-sent OTP.
      setShowVerifyOtpAction(hasRequestedOtp);
    } finally {
      setIsSendingVerification(false);
    }
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="bg-nova-dashboard-900 min-h-full md:min-h-screen">
      {/* Main Content Area */}
      <div
        className={
          isProfileSubRoute
            ? "px-4 pb-4 pt-0 sm:px-6 sm:pb-6 sm:pt-3 md:px-8 md:pb-8 md:pt-4"
            : "p-4 sm:p-6 md:p-8"
        }
      >
        {/* Header */}
        <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">Profile Info</span>

        {/* Permission Notice - Show if user doesn't have edit permission */}
        {!canEditPermission && (
          <div className="mt-6 mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-yellow-800 font-family-messina-modern mb-1">
                  View Only Mode
                </h3>
                <p className="text-sm text-yellow-700 font-family-messina-sans">
                  You don't have permission to edit this profile. You can view the information but cannot make changes. Please contact your administrator if you need edit access.
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Account Information Section */}
      <div className={isAccountRoute ? "mb-6 sm:mb-8 mt-0" : "mb-6 sm:mb-8 mt-[3rem]"}>
        {/* Hidden title for section hide/show logic (ProfileSectionedArtistProfile). */}
        <span className="hidden">Account Information</span>
        <div
          className={
            isAccountRoute
              ? "sticky top-0 z-20 -mx-4 sm:-mx-6 md:-mx-8 px-4 sm:px-6 md:px-8 py-2.5 sm:py-3.5 mb-4 sm:mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-gray-300 bg-nova-dashboard-900 shadow-[0_1px_3px_rgba(0,0,0,0.06)]"
              : "mb-6"
          }
        >
          <span className="text-[1.4375rem] font-regular text-gray-900 font-family-messina-modern shrink-0">
            Account Information
          </span>
          {canEditPermission && isAccountRoute && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 items-stretch w-full sm:w-auto">
              {!isEditingAccount ? (
                <button
                  type="button"
                  onClick={() => {
                    // Clear any old validation state when entering edit mode.
                    resetAccountEdits();
                    setIsEditingAccount(true);
                  }}
                  disabled={isSubmitting}
                  aria-label="Edit account information"
                  className="bg-black text-white px-4 py-3 rounded-lg-settings font-medium hover:bg-gray-800 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed font-family-messina-sans w-full sm:w-auto flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/20"
                >
                  <Pencil size={14} strokeWidth={2} />
                  <span className="text-[0.6875rem] leading-none">EDIT</span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      resetAccountEdits();
                      setIsEditingAccount(false);
                    }}
                    disabled={isSubmitting}
                    className="bg-white text-gray-900 text-[0.6875rem] px-8 py-3 rounded-lg-settings font-medium border border-gray-300 hover:bg-gray-100 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed font-family-messina-sans w-full sm:w-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/20 flex items-center justify-center gap-2"
                  >
                    <X size={14} strokeWidth={2} />
                      <span className="leading-none">CANCEL</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSaveChanges('profile')}
                    disabled={
                      isSubmitting ||
                      !hasProfileChanges() ||
                      Object.values(errors).some(error => error !== '')
                    }
                    className="bg-black text-white text-[0.6875rem] px-8 py-3 rounded-lg-settings font-medium hover:bg-gray-800 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed font-family-messina-sans w-full sm:w-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/20 flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Save size={14} strokeWidth={2} className="opacity-80" />
                          <span className="leading-none">SAVING...</span>
                      </>
                    ) : (
                      <>
                        <Save size={14} strokeWidth={2} />
                          <span className="leading-none">SAVE CHANGES</span>
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4 sm:space-y-6 font-family-messina-sans-regular">
          {/* Artist Name — same field as Artist Information (user_artists.artist_name) */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              ARTIST NAME
            </label>
            <input
              type="text"
              value={profileData.artistName}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                const value = e.target.value;
                setProfileData({...profileData, artistName: value});
                setErrors(prev => ({
                  ...prev,
                  artistName: validateProfileField("artistName", value, profileValidationAvailability),
                }));
              }}
              className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none font-family-messina-sans ${
                !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
              } ${
                errors.artistName ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gray-900'
              }`}
            />
            {errors.artistName && (
              <p className="text-red-500 text-xs mt-1">{errors.artistName}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              EMAIL
            </label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
              <input
                type="email"
                value={profileData.email}
                readOnly
                onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                className={`flex-1 w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                  emailVerified ? "profile-input-email-verified" : "profile-input-email-unverified"
                }`}
              />
              {emailVerified ? (
                <div className="flex items-center gap-2 text-green-600 whitespace-nowrap self-start sm:self-auto">
                  <CheckCircle size={16} />
                  <span className="text-[0.625rem] sm:text-[0.625rem]">EMAIL VERIFIED</span>
                </div>
              ) : (
              <button
                onClick={handleSendVerificationCode}
                disabled={isSendingVerification}
                className="flex items-center gap-2 profile-input-email-unverified hover:font-semibold transition-all duration-500 ease-in-out whitespace-nowrap self-start sm:self-auto disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="text-[0.625rem] sm:text-[0.625rem]">
                  {isSendingVerification ? "SENDING..." : "SEND VERIFICATION CODE"}
                </span>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isSendingVerification ? 'border border-[#920000]' : 'border border-[#920000]'
                }`}>
                  {isSendingVerification ? (
                    <div className="w-3 h-3 border-2 border-[#920000] border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <ExternalLink size={12} className="text-[#920000]" />
                  )}
                </div>
              </button>
              )}
            </div>
          </div>

          {/* Current Password */}
          {/* <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              CURRENT PASSWORD
            </label>
            <input
              type="password"
              value="******************"
              readOnly
              className="w-full text-lg text-gray-900 bg-transparent border-b border-gray-300 pb-2 font-family-messina-sans"
            />
          </div> */}

          {/* Mobile Number */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              MOBILE NUMBER
            </label>
            <div className="flex gap-2">
              {/* Country Code Dropdown */}
              <CountryCodeDropdown
                value={selectedCountryCode}
                onChange={(value) => {
                  setSelectedCountryCode(value);
                  // Re-check mobile availability when country code changes with new country code
                  const cleanNumber = profileData.mobile.replace(/\D/g, '');
                  if (cleanNumber && cleanNumber.length >= 10 && cleanNumber.length <= 12) {
                    checkMobileAvailability(cleanNumber, value);
                  }
                }}
                className={`flex-shrink-0 ${canEditProfile ? "" : "pointer-events-none opacity-60 cursor-not-allowed"}`}
                style={{ 
                  minWidth: '120px'
                }}
              />
              
              {/* Mobile Number Input */}
              <input
                type="tel"
                value={profileData.mobile}
                disabled={!canEditProfile}
                maxLength={12}
                onChange={(e) => {
                  if (!canEditProfile) return;
                  // Remove all non-digit characters and limit to 12 digits
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 12);
                  setProfileData(prev => ({ ...prev, mobile: digits }));
                  if (errors.mobile) {
                    setErrors(prev => ({...prev, mobile: ''}));
                  }
                }}
                onBlur={() => {
                  if (!canEditProfile) return;
                  setErrors(prev => ({
                    ...prev,
                    mobile: validateProfileField("mobile", profileData.mobile, profileValidationAvailability),
                  }));
                }}
                placeholder="Enter your mobile number"
                className={`flex-1 text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none font-family-messina-sans ${
                  !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
                } ${
                  errors.mobile ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gray-900'
                }`}
              />
            </div>
            {errors.mobile && (
              <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>
            )}
          </div>
        
        {/* Upload Profile Pic */}
        <div>
          <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
            UPLOAD PROFILE PIC
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            {/* Left: Upload field */}
            <div>
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                disabled={!canEditProfile}
                onChange={(e) => {
                  if (!canEditProfile) return;
                  const file = e.target.files?.[0];
                  if (!file) return;
                  
                  const validTypes = ["image/jpeg", "image/jpg", "image/png"];
                  if (!validTypes.includes(file.type)) {
                    toast.error("Only JPG, JPEG, PNG files are allowed");
                    return;
                  }
                  if (file.size > 20 * 1024 * 1024) {
                    toast.error("File too large (max 20MB)");
                    return;
                  }
                  
                  // Open crop modal instead of uploading directly
                  setImageToCrop(file);
                  setShowCropModal(true);
                  
                  // Reset file input
                  e.target.value = '';
                }}
                className={`w-full text-sm text-gray-500 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                  !canEditProfile ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer hover:opacity-90'
                }`}
              />
              
              {isUploadingProfilePic && (
                <div className="mt-2 flex items-center gap-3">
                  <div className="w-36 h-2 bg-gray-200 rounded">
                    <div className="h-2 bg-gray-900 rounded transition-all" style={{ width: `${profilePicUploadProgress}%` }} />
                  </div>
                  <span className="text-xs text-gray-600">{profilePicUploadProgress}%</span>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-gray-900"></div>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-1">Allowed types: JPG, JPEG, PNG</p>
            </div>

            {/* Right: Preview */}
            <div className="flex md:justify-end">
              {profilePicPreviewUrl ? (
                <img
                  src={profilePicPreviewUrl}
                  alt="Profile Preview"
                  className="w-24 h-24 md:w-32 md:h-32 rounded-md object-cover border border-gray-200"
                />
              ) : (
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-md border border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs">
                  No Image
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Image Cropper Modal */}
        {showCropModal && imageToCrop && (
          <ImageCropper
            image={imageToCrop}
            isOpen={showCropModal}
            onClose={() => {
              setShowCropModal(false);
              setImageToCrop(null);
            }}
            onCropComplete={async (croppedImageBlob) => {
              try {
                const uid = auth.currentUser?.uid;
                if (!uid) {
                  toast.error("You must be signed in to upload a profile picture");
                  return;
                }
                
                const ext = 'jpg'; // Always use jpg for cropped images
                const id = (globalThis.crypto && (crypto as any).randomUUID) ? (crypto as any).randomUUID() : `${Date.now()}`;
                const path = `users/${uid}/profile_${id}.${ext}`;
                const ref = storageRef(storage, path);

                setIsUploadingProfilePic(true);
                setProfilePicUploadProgress(0);

                // Clear any existing timeout
                if (uploadTimeoutRef.current) {
                  clearTimeout(uploadTimeoutRef.current);
                }

                const task = uploadBytesResumable(ref, croppedImageBlob, { contentType: 'image/jpeg' });
                uploadTaskRef.current = task;

                // Set upload timeout: 5 minutes
                uploadTimeoutRef.current = setTimeout(() => {
                  if (uploadTaskRef.current) {
                    uploadTaskRef.current.cancel();
                    uploadTaskRef.current = null;
                    setIsUploadingProfilePic(false);
                    setProfilePicUploadProgress(0);
                    toast.error("Upload timeout. Please check your connection and try again.");
                  }
                }, 5 * 60 * 1000); // 5 minutes

                task.on('state_changed', (snapshot) => {
                  const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                  setProfilePicUploadProgress(progress);
                }, (error) => {
                  console.error("Upload failed", error);
                  // Clear timeout on error
                  if (uploadTimeoutRef.current) {
                    clearTimeout(uploadTimeoutRef.current);
                    uploadTimeoutRef.current = null;
                  }
                  uploadTaskRef.current = null;
                  setIsUploadingProfilePic(false);
                  
                  // Show specific error message for cancellation
                  if (error.code === 'storage/canceled') {
                    toast.error("Upload was canceled");
                  } else {
                    toast.error("Failed to upload image. Please try again.");
                  }
                }, async () => {
                  // Clear timeout on success
                  if (uploadTimeoutRef.current) {
                    clearTimeout(uploadTimeoutRef.current);
                    uploadTimeoutRef.current = null;
                  }
                  uploadTaskRef.current = null;
                  
                  const url = await getDownloadURL(ref);
                  setProfileData(prev => ({ ...prev, profilePic: path }));
                  setProfilePicPreviewUrl(url);
                  setIsUploadingProfilePic(false);
                  toast.success("Image uploaded. Click Save Changes to apply.");
                });
              } catch (err) {
                console.error("Upload failed", err);
                toast.error("Failed to upload image");
                setIsUploadingProfilePic(false);
              }
            }}
            aspect={1}
            cropShape="round"
            outputSize={{ width: 800, height: 800 }}
            quality={0.9}
          />
        )}

          {/* Address */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              ADDRESS
            </label>
            <input
              type="text"
              value={profileData.address}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                const value = e.target.value;
                setProfileData({...profileData, address: value});
                setErrors(prev => ({
                  ...prev,
                  address: validateProfileField("address", value, profileValidationAvailability),
                }));
              }}
              onBlur={() => {
                if (!canEditProfile) return;
                setErrors(prev => ({
                  ...prev,
                  address: validateProfileField("address", profileData.address, profileValidationAvailability),
                }));
              }}
              className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none font-family-messina-sans ${
                !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
              } ${
                errors.address ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gray-900'
              }`}
            />
            {errors.address && (
              <p className="text-red-500 text-xs mt-1">{errors.address}</p>
            )}
          </div>

          {/* Address Section */}
          <div className="mt-8">
            {/* <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Address</span> */}
            
            {/* Address Fields - 2 columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* Country */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                COUNTRY
              </label>
              <input
                type="text"
                value={profileData.country}
                disabled={!canEditProfile}
                onChange={(e) => {
                  if (!canEditProfile) return;
                  setProfileData(prev => ({ ...prev, country: e.target.value }));
                }}
                placeholder="Enter country"
                className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                  !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              />
            </div>

            {/* State */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                STATE
              </label>
              <input
                type="text"
                value={profileData.state}
                disabled={!canEditProfile}
                onChange={(e) => {
                  if (!canEditProfile) return;
                  setProfileData(prev => ({ ...prev, state: e.target.value }));
                }}
                placeholder="Enter state"
                className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                  !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                CITY
              </label>
              <input
                type="text"
                value={profileData.city}
                disabled={!canEditProfile}
                onChange={(e) => {
                  if (!canEditProfile) return;
                  setProfileData(prev => ({ ...prev, city: e.target.value }));
                }}
                placeholder="Enter city"
                className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                  !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              />
            </div>

            {/* Street */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                STREET
              </label>
              <input
                type="text"
                value={profileData.street}
                disabled={!canEditProfile}
                onChange={(e) => {
                  if (!canEditProfile) return;
                  setProfileData(prev => ({ ...prev, street: e.target.value }));
                }}
                placeholder="Enter street address"
                className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                  !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              />
            </div>

            {/* ZIP Code */}
            <div>
              <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                ZIP CODE
              </label>
              <input
                type="text"
                value={profileData.zipCode}
                disabled={!canEditProfile}
                onChange={(e) => {
                  if (!canEditProfile) return;
                  setProfileData(prev => ({ ...prev, zipCode: e.target.value }));
                }}
                placeholder="Enter ZIP code"
                className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                  !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              />
            </div>
            </div>
          </div>
          
          {/* Shirt Size */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              SHIRT SIZE
            </label>
            <select
              value={profileData.shirtSize}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                setProfileData(prev => ({ ...prev, shirtSize: e.target.value }));
              }}
              className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            >
              <option value="">Select shirt size</option>
              <option value="2XS">2XS</option>
              <option value="XS">XS</option>
              <option value="S">S</option>
              <option value="M">M</option>
              <option value="L">L</option>
              <option value="XL">XL</option>
              <option value="2XL">2XL</option>
              <option value="3XL">3XL</option>
              <option value="4XL">4XL</option>
              <option value="5XL">5XL</option>
            </select>
          </div>

          {/* Reset Password */}
          {/* <div>
            <button
              onClick={handleResetPassword}
              className="flex items-center gap-2 text-gray-900 hover:text-gray-700 transition-all duration-500 ease-in-out"
            >
              <span className="text-sm">RESET PASSWORD</span>
              <div className="w-6 h-6 rounded-full bg-gray-900 flex items-center justify-center">
                <ExternalLink size={12} className="text-white" />
              </div>
            </button>
          </div> */}
        </div>

      </div>

      {/* Change Password Section */}
      <div className="mb-6 sm:mb-8 mt-[4rem]">
        {/* Hidden title for section hide/show logic (ProfileSectionedArtistProfile). */}
        <span className="hidden">Change Password</span>
        <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Change Password</span>
        
        <div className="space-y-4 sm:space-y-6 font-family-messina-sans-regular">
          {/* Verification Code */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              VERIFICATION CODE
            </label>
            <div className="relative">
              <input
                type="text"
                value={verificationCode}
                disabled={!canEditProfile || !hasRequestedOtp || isPasswordVerificationActive}
                onChange={(e) => {
                  if (!canEditProfile) return;
                  if (isPasswordVerificationActive) return;
                  setVerificationCode(e.target.value);
                  setOtpVerificationStatus('idle');
                  setVerifiedOtpValue("");
                  setShowVerifyOtpAction(hasRequestedOtp);
                }}
                placeholder=""
                className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 pr-32 font-family-messina-sans ${
                  (!canEditProfile || isPasswordVerificationActive) ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              />
              {hasRequestedOtp && showVerifyOtpAction && otpVerificationStatus !== 'verified' && (
                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={isVerifyingOtp || !canEditProfile}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[0.625rem] sidebar-menu-item bg-black text-white px-3 py-1 rounded-lg hover:bg-gray-800 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isVerifyingOtp ? "VERIFYING..." : "Verify Code"}
                </button>
              )}
            </div>
          </div>

          {/* Resend Code */}
          <div>
            <div className="flex items-center gap-4 flex-wrap">
              {otpVerificationStatus !== 'verified' && (
                <button
                  onClick={handleResendCode}
                  disabled={isSendingVerification || resendCountdown > 0 || !canEditProfile}
                  className="flex items-center gap-2 text-gray-900 hover:text-gray-700 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="text-[0.625rem] sidebar-menu-item">
                    {isSendingVerification
                      ? "SENDING..."
                      : hasRequestedOtp
                        ? (resendCountdown > 0 ? `RESEND OTP IN ${resendCountdown}S` : "RESEND CODE")
                        : "REQUEST CODE"}
                  </span>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isSendingVerification ? 'bg-gray-400' : 'bg-gray-900'
                  }`}>
                    {isSendingVerification ? (
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <ExternalLink size={12} className="text-white" />
                    )}
                  </div>
                </button>
              )}

              {isPasswordVerificationActive && (
                <span className="text-green-600 text-[0.625rem] sidebar-menu-item flex items-center gap-1">
                  <CheckCircle size={12} />
                  CODE VERIFIED
                </span>
              )}
              {otpVerificationStatus === 'failed' && (
                <span className="text-red-600 text-[0.625rem] sidebar-menu-item">
                  Invalid or expired OTP
                </span>
              )}
            </div>
            {hasRequestedOtp && otpSentToLabel && otpVerificationStatus !== 'verified' && (
              <p className="text-[0.625rem] text-gray-600 mt-2 sidebar-menu-item">
                OTP sent to {otpSentToLabel}
              </p>
            )}
            {isPasswordVerificationActive && verificationSessionCountdown > 0 && (
              <p className="text-[0.625rem] text-green-700 mt-2 sidebar-menu-item">
                Verified just now, valid for {formatSecondsToMinutes(verificationSessionCountdown)}
              </p>
            )}
            {hasRequestedOtp && resendCountdown === 0 && !isSendingVerification && otpVerificationStatus !== 'verified' && (
              <p className="text-[0.625rem] text-gray-600 mt-2 sidebar-menu-item">
                Didn’t receive the Code?{" "}
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={!canEditProfile}
                  className="underline text-gray-900 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Resend
                </button>
              </p>
            )}
          </div>

          {/* Password Fields - revealed only after OTP verification */}
          {isPasswordVerificationActive ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {/* New Password */}
              <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  NEW PASSWORD
                </label>
                <input
                  type="password"
                  value={newPassword}
                  disabled={!canEditProfile}
                  onChange={(e) => {
                    if (!canEditProfile) return;
                    handlePasswordChange('newPassword', e.target.value);
                  }}
                  className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none font-family-messina-sans ${
                    !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
                  } ${
                    passwordErrors.newPassword ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gray-900'
                  }`}
                />
                {passwordErrors.newPassword && (
                  <p className="text-red-500 text-xs mt-1">{passwordErrors.newPassword}</p>
                )}
                <p
                  className={`mt-1.5 text-[0.625rem] font-family-messina-sans flex items-center gap-1 transition-colors ${
                    newPassword.trim().length >= 6 ? "text-green-600" : "text-gray-400"
                  }`}
                >
                  {newPassword.trim().length >= 6 ? (
                    <CheckCircle className="w-3 h-3 shrink-0" aria-hidden />
                  ) : null}
                  <span>At least 6 characters</span>
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
                  CONFIRM PASSWORD
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  disabled={!canEditProfile}
                  onChange={(e) => {
                    if (!canEditProfile) return;
                    handlePasswordChange('confirmPassword', e.target.value);
                  }}
                  className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none font-family-messina-sans ${
                    !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
                  } ${
                    passwordErrors.confirmPassword ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gray-900'
                  }`}
                />
                {passwordErrors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">{passwordErrors.confirmPassword}</p>
                )}
                <p
                  className={`mt-1.5 text-[0.625rem] font-family-messina-sans flex items-center gap-1 transition-colors ${
                    confirmPassword.trim() !== "" && newPassword === confirmPassword
                      ? "text-green-600"
                      : "text-gray-400"
                  }`}
                >
                  {confirmPassword.trim() !== "" && newPassword === confirmPassword ? (
                    <CheckCircle className="w-3 h-3 shrink-0" aria-hidden />
                  ) : null}
                  <span>Same as new password</span>
                </p>
              </div>
            </div>
              <div>
                <button
                  onClick={() => handleSaveChanges('password')}
                  disabled={isSubmitting || !isPasswordSectionComplete}
                  className="bg-black text-white text-[0.625rem] px-8 py-3 rounded-lg-settings font-medium hover:bg-gray-800 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed font-family-messina-sans"
                >
                  {isSubmitting ? "SAVING..." : "SAVE PASSWORD"}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* Artist Route (keep sticky header stable across sub-sections) */}
      <div>
        {/* Hidden title for section hide/show logic (ProfileSectionedArtistProfile). */}
        <span className="hidden">Artist Information</span>

        {isArtistProfileRoute && (
          <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 md:-mx-8 px-4 sm:px-6 md:px-8 py-3.5 mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-gray-300 bg-nova-dashboard-900 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
            <span className="text-[1.4375rem] font-regular text-gray-900 font-family-messina-modern shrink-0">
              Artist Information
            </span>
            {canEditPermission && isArtistProfileRoute && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 items-stretch w-full sm:w-auto">
                {!isEditingArtist ? (
                  <button
                    type="button"
                    onClick={() => {
                      resetAccountEdits();
                      setIsEditingArtist(true);
                    }}
                    disabled={isSubmitting}
                    aria-label="Edit artist information"
                    className="bg-black text-white px-4 py-3 rounded-lg-settings font-medium hover:bg-gray-800 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed font-family-messina-sans w-full sm:w-auto flex items-center justify-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/20"
                  >
                    <Pencil size={14} strokeWidth={2} />
                    <span className="text-[0.6875rem] leading-none">EDIT</span>
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        resetAccountEdits();
                        setIsEditingArtist(false);
                      }}
                      disabled={isSubmitting}
                      className="bg-white text-gray-900 text-[0.6875rem] px-8 py-3 rounded-lg-settings font-medium border border-gray-300 hover:bg-gray-100 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed font-family-messina-sans w-full sm:w-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/20 flex items-center justify-center gap-2"
                    >
                      <X size={14} strokeWidth={2} />
                      <span className="leading-none">CANCEL</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSaveChanges('profile')}
                      disabled={
                        isSubmitting ||
                        !hasProfileChanges() ||
                        Object.values(errors).some(error => error !== '')
                      }
                      className="bg-black text-white text-[0.6875rem] px-8 py-3 rounded-lg-settings font-medium hover:bg-gray-800 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed font-family-messina-sans w-full sm:w-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900/20 flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Save size={14} strokeWidth={2} className="opacity-80" />
                          <span className="leading-none">SAVING...</span>
                        </>
                      ) : (
                        <>
                          <Save size={14} strokeWidth={2} />
                          <span className="leading-none">SAVE CHANGES</span>
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Artist Information Section */}
        <div className="mb-6 sm:mb-8 mt-0">
          {/* Hidden title for section hide/show logic (ProfileSectionedArtistProfile). */}
          <span className="hidden">Artist Information</span>

          <div className="space-y-4 sm:space-y-6 font-family-messina-sans-regular">
          {/* First Name */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              FIRST NAME
            </label>
            <input
              type="text"
              value={profileData.firstName}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                const value = e.target.value;
                setProfileData({...profileData, firstName: value});
                setErrors(prev => ({
                    ...prev,
                    firstName: validateProfileField("firstName", value, profileValidationAvailability),
                  }));
              }}
              className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none font-family-messina-sans ${
                !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
              } ${
                errors.firstName ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gray-900'
              }`}
            />
            {errors.firstName && (
              <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
            )}
          </div>

          {/* Last Name (optional) */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              LAST NAME 
            </label>
            <input
              type="text"
              value={profileData.lastName}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                const value = e.target.value;
                setProfileData({...profileData, lastName: value});
                setErrors(prev => ({
                    ...prev,
                    lastName: validateProfileField("lastName", value, profileValidationAvailability),
                  }));
              }}
              className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none font-family-messina-sans ${
                !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
              } ${
                errors.lastName ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gray-900'
              }`}
            />
            {errors.lastName && (
              <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
            )}
          </div>

          {/* Artist Name */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              ARTIST NAME
            </label>
            <input
              type="text"
              value={profileData.artistName}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                const value = e.target.value;
                setProfileData({...profileData, artistName: value});
                setErrors(prev => ({
                    ...prev,
                    artistName: validateProfileField("artistName", value, profileValidationAvailability),
                  }));
              }}
              className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none font-family-messina-sans ${
                !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
              } ${
                errors.artistName ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gray-900'
              }`}
            />
            {errors.artistName && (
              <p className="text-red-500 text-xs mt-1">{errors.artistName}</p>
            )}
          </div>
          </div>
        </div>

      {/* Distribution Information Section */}
      <div className="mb-6 sm:mb-8 mt-[4rem]">
        {/* Hidden title for section hide/show logic (ProfileSectionedArtistProfile). */}
        <span className="hidden">Distribution Information</span>
        <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Distribution Information</span>
        
        <div className="space-y-4 sm:space-y-6 font-family-messina-sans-regular">
          {/* Distributors - Multiple Selection with Tags */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              DISTRIBUTORS
            </label>
            
            {/* Display existing distributors as tags */}
            {distributors.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {distributors.map((distributor, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-900 rounded-full text-sm font-family-messina-sans"
                  >
                    {distributor}
                    <button
                      type="button"
                      onClick={() => {
                        if (!canEditProfile) return;
                        removeDistributor(index);
                      }}
                      disabled={!canEditProfile}
                      className={`ml-1 text-gray-500 hover:text-gray-700 focus:outline-none ${
                        !canEditProfile ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      aria-label={`Remove ${distributor}`}
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            
            {/* Add Distributor - Dropdown Only */}
            <div className="space-y-3">
              <div className="flex gap-2">
                <select
                  value={selectedDistributorDropdown}
                  disabled={!canEditProfile}
                  onChange={(e) => {
                    if (!canEditProfile) return;
                    handleDistributorSelect(e.target.value);
                  }}
                  className={`flex-1 text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                    !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="">Select distributor to add</option>
                  {PLATFORMS.filter(platform => 
                    platform.name !== "Other" && 
                    !distributors.some(d => d.toLowerCase() === platform.name.toLowerCase())
                  ).map((platform) => (
                    <option key={platform.id} value={platform.name}>
                      {platform.name}
                    </option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              </div>
              
              {/* Custom Distributor Input - Only show when "Other" is selected */}
              {selectedDistributorDropdown === "Other" && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={customDistributorInput}
                    disabled={!canEditProfile}
                    onChange={(e) => {
                      if (!canEditProfile) return;
                      setCustomDistributorInput(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (!canEditProfile) return;
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomDistributor();
                      }
                    }}
                    placeholder="Enter custom distributor name"
                    className={`flex-1 text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                      !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomDistributor}
                    disabled={!canEditProfile}
                    className={`px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans ${
                      !canEditProfile ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!canEditProfile) return;
                      setSelectedDistributorDropdown("");
                      setCustomDistributorInput("");
                    }}
                    disabled={!canEditProfile}
                    className={`px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans ${
                      !canEditProfile ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            
            {distributors.length === 0 && (
              <p className="text-gray-500 text-xs mt-2">No distributors added yet</p>
            )}
          </div>
        </div>
      </div>

      {/* Artist Account Links Section */}
      <div className="mb-6 sm:mb-8 mt-[4rem]">
        {/* Hidden title for section hide/show logic (ProfileSectionedArtistProfile). */}
        <span className="hidden">Artist Account Links</span>
        <span className="text-[1.4375rem] font-regular text-gray-900 mb-6 block font-family-messina-modern">Artist Account Links</span>
        
        <div className="space-y-4 sm:space-y-6 font-family-messina-sans-regular">

          {/* YouTube URL */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              YOUTUBE URL
            </label>
                    <input
                      type="url"
              value={profileData.youtubeUrl}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                setProfileData(prev => ({ ...prev, youtubeUrl: e.target.value }));
              }}
              placeholder="https://youtube.com/@yourchannel"
              className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans ${
                !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
              }`}
            />
                  </div>

          {/* Spotify URL */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              SPOTIFY URL
            </label>
                            <input
                              type="url"
              value={profileData.spotifyUrl}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                const value = e.target.value;
                setProfileData(prev => ({ ...prev, spotifyUrl: value }));
                setErrors(prev => ({
                  ...prev,
                  spotifyUrl: validateProfileField("spotifyUrl", value, profileValidationAvailability),
                }));
              }}
              onBlur={() => {
                if (!canEditProfile) return;
                setErrors(prev => ({
                  ...prev,
                  spotifyUrl: validateProfileField(
                    "spotifyUrl",
                    profileData.spotifyUrl,
                    profileValidationAvailability
                  ),
                }));
              }}
              placeholder="https://open.spotify.com/artist/yourid"
              className={`w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b pb-2 focus:outline-none font-family-messina-sans ${
                !canEditProfile ? 'opacity-60 cursor-not-allowed' : ''
              } ${
                errors.spotifyUrl ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-gray-900'
              }`}
            />
            {errors.spotifyUrl && (
              <p className="text-red-500 text-xs mt-1">{errors.spotifyUrl}</p>
            )}
                          </div>

          {/* Apple URL */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              APPLE URL
            </label>
            <input
              type="url"
              value={profileData.appleUrl}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                setProfileData(prev => ({ ...prev, appleUrl: e.target.value }));
              }}
              placeholder="https://music.apple.com/artist/yourid"
              className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
            />
          </div>

          {/* Instagram Link */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              INSTAGRAM LINK / META
            </label>
            <input
              type="url"
              value={profileData.instagramUrl}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                setProfileData(prev => ({ ...prev, instagramUrl: e.target.value }));
              }}
              placeholder="https://instagram.com/yourhandle"
              className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
            />
          </div>

          {/* Facebook */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              FACEBOOK
            </label>
                                <input
              type="url"
              value={profileData.facebookUrl}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                setProfileData(prev => ({ ...prev, facebookUrl: e.target.value }));
              }}
              placeholder="https://facebook.com/yourpage"
              className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
                                />
                              </div>

          {/* TikTok Link */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              TIKTOK LINK
            </label>
            <input
              type="url"
              value={profileData.tiktokUrl}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                setProfileData(prev => ({ ...prev, tiktokUrl: e.target.value }));
              }}
              placeholder="https://tiktok.com/@yourhandle"
              className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
            />
                          </div>

          {/* SoundCloud Link */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              SOUNDCLOUD LINK
            </label>
            <input
              type="url"
              value={profileData.soundcloudUrl}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                setProfileData(prev => ({ ...prev, soundcloudUrl: e.target.value }));
              }}
              placeholder="https://soundcloud.com/yourhandle"
              className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
            />
                    </div>

          {/* X / Twitter */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              X / TWITTER
            </label>
            <input
              type="url"
              value={profileData.twitterUrl}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                setProfileData(prev => ({ ...prev, twitterUrl: e.target.value }));
              }}
              placeholder="https://x.com/yourhandle"
              className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
            />
                          </div>

          {/* Amazon Music */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              AMAZON MUSIC
            </label>
            <input
              type="url"
              value={profileData.amazonMusicUrl}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                setProfileData(prev => ({ ...prev, amazonMusicUrl: e.target.value }));
              }}
              placeholder="https://music.amazon.com/artists/yourid"
              className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
            />
                    </div>

          {/* Deezer */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              DEEZER
            </label>
            <input
              type="url"
              value={profileData.deezerUrl}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                setProfileData(prev => ({ ...prev, deezerUrl: e.target.value }));
              }}
              placeholder="https://www.deezer.com/en/artist/yourid"
              className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
            />
              </div>

          {/* Audiomack */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              AUDIOMACK
            </label>
            <input
              type="url"
              value={profileData.audiomackUrl}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                setProfileData(prev => ({ ...prev, audiomackUrl: e.target.value }));
              }}
              placeholder="https://audiomack.com/yourhandle"
              className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
            />
            </div>

          {/* Pandora */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              PANDORA
            </label>
            <input
              type="url"
              value={profileData.pandoraUrl}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                setProfileData(prev => ({ ...prev, pandoraUrl: e.target.value }));
              }}
              placeholder="https://pandora.com/artist/your-name"
              className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
            />
          </div>

          {/* Tidal */}
          <div>
            <label className="block text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide mb-2 sidebar-menu-item">
              TIDAL
            </label>
            <input
              type="url"
              value={profileData.tidalUrl}
              disabled={!canEditProfile}
              onChange={(e) => {
                if (!canEditProfile) return;
                setProfileData(prev => ({ ...prev, tidalUrl: e.target.value }));
              }}
              placeholder="https://tidal.com/browse/artist/yourid"
              className="w-full text-[1.375rem] md:text-[1.375rem] sm:text-lg text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 font-family-messina-sans"
            />
          </div>
        </div>
      </div>

      

        {/* Save Changes Button — bottom placement on full profile; sticky headers handle Account/Artist */}
        <div className="mt-8">
          {canEditProfile && !isChangePasswordRoute && !isAccountRoute && !isArtistProfileRoute ? (
            <>
          <button
            onClick={() => handleSaveChanges('profile')}
            disabled={
              isSubmitting ||
              !hasProfileChanges() ||
              Object.values(errors).some(error => error !== '')
            }
            className="bg-black text-white text-[0.625rem] px-8 py-3 rounded-lg-settings font-medium hover:bg-gray-800 transition-all duration-500 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed font-family-messina-sans"
          >
            {isSubmitting ? "SAVING..." : "SAVE CHANGES"}
          </button>
            </>
          ) : (
            <></>
            // <div className="p-4 bg-gray-100 rounded-lg border border-gray-300">
            //   <p className="text-sm text-gray-600 font-family-messina-sans">
            //     {/* <strong className="font-semibold">Edit permission required:</strong> You need the <code className="bg-gray-200 px-1 rounded">profile:edit</code> permission to make changes to this profile. */}
            //   </p>
            // </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default ArtistProfile;
